import type { StaffAction } from "@payezy/api-client/admin";
import { decideActionMutation, issueViewAsTokenMutation, listActionsOptions } from "@payezy/api-client/admin/query";
import { Badge, Button, Card, Dialog, EmptyState, Field, formatDate, humanize, PageHeader, shortId, Tabs, Table, TD, Textarea, TH, THead, TR, toast } from "@payezy/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { showError } from "../lib/notify";
import { actionTone, useStaff } from "../lib/staff";

export function ActionsPage() {
  const [status, setStatus] = useState("pending_approval");
  const q = useQuery({ ...listActionsOptions({ query: { status: status === "all" ? undefined : status, limit: 200 } }), placeholderData: keepPreviousData, refetchInterval: 15_000 });
  const items = q.data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Actions & approvals"
        description="Every act-on-behalf change with its reason. Anything that loosens a control needs a second staff member; you can't approve your own request."
      />
      <Tabs value={status} onValueChange={setStatus} tabs={["pending_approval", "executed", "failed", "rejected", "all"].map((s) => ({ value: s, label: humanize(s) }))} />
      <Card>
        <Table>
          <THead><TR><TH>Requested</TH><TH>Action</TH><TH>Customer</TH><TH>Requested by</TH><TH>Reason</TH><TH>Status</TH><TH /></TR></THead>
          <tbody>
            {items.map((a) => <ActionRow key={a.id} a={a} />)}
          </tbody>
        </Table>
        {!q.isLoading && items.length === 0 && <EmptyState title="Nothing here" />}
      </Card>
    </>
  );
}

function payloadSummary(a: StaffAction): string {
  const p = a.payload as Record<string, unknown>;
  return Object.entries(p)
    .filter(([k]) => !k.endsWith("_id"))
    .map(([k, v]) => `${humanize(k)}: ${String(v)}`)
    .join(" · ");
}

function ActionRow({ a }: { a: StaffAction }) {
  const me = useStaff();
  const [deciding, setDeciding] = useState<boolean | null>(null);
  const sessionId = (a.result as { session_id?: string } | undefined)?.session_id;
  const openSession = useMutation({
    ...issueViewAsTokenMutation(),
    onSuccess: (g) => window.open(g.url, "_blank", "noopener,noreferrer"),
    onError: showError,
  });
  return (
    <TR>
      <TD>{formatDate(a.created_at, true)}</TD>
      <TD>
        <p className="font-medium">{a.title ?? a.type}</p>
        {payloadSummary(a) && <p className="text-xs text-ink-500">{payloadSummary(a)}</p>}
        {a.error && <p className="text-xs text-red-600">{a.error}</p>}
      </TD>
      <TD>{a.org_id ? <Link to="/customers/$orgId" params={{ orgId: a.org_id }} className="font-mono text-xs hover:underline">{shortId(a.org_id)}</Link> : "—"}</TD>
      <TD>{a.requested_by_email}</TD>
      <TD className="max-w-72"><p className="line-clamp-2 text-sm" title={a.reason}>{a.reason}</p></TD>
      <TD>
        <Badge tone={actionTone[a.status]}>{humanize(a.status)}</Badge>
        {a.decided_by_email && <p className="mt-1 text-xs text-ink-500">by {a.decided_by_email}</p>}
      </TD>
      <TD className="whitespace-nowrap text-right">
        {a.can_decide && (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setDeciding(false)}>Reject</Button>
            <Button size="sm" onClick={() => setDeciding(true)}>Approve</Button>
          </div>
        )}
        {a.type === "impersonation.write" && a.status === "executed" && a.requested_by === me.id && sessionId && (
          <Button size="sm" loading={openSession.isPending} onClick={() => openSession.mutate({ path: { sessionId } })}>Open session</Button>
        )}
        {deciding !== null && <DecideDialog a={a} approve={deciding} onClose={() => setDeciding(null)} />}
      </TD>
    </TR>
  );
}

function DecideDialog({ a, approve, onClose }: { a: StaffAction; approve: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const decide = useMutation({
    ...decideActionMutation(),
    onSuccess: (r) => {
      onClose();
      void qc.invalidateQueries();
      if (r.status === "failed") toast.error(r.error || "Approved, but the action failed.");
      else toast.success(approve ? "Approved and executed." : "Rejected.");
    },
    onError: showError,
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${approve ? "Approve" : "Reject"}: ${a.title ?? a.type}`}
      description={`Requested by ${a.requested_by_email}: “${a.reason}”`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant={approve ? "primary" : "danger"} loading={decide.isPending} onClick={() => decide.mutate({ path: { actionId: a.id }, body: { approve, note } })}>
            {approve ? "Approve and run" : "Reject"}
          </Button>
        </>
      }
    >
      <Field label="Note" hint="Optional; recorded with your decision.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}
